import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Brain, Clock, CheckCircle2, IndianRupee, Calendar as CalendarIcon, QrCode, FileText, ExternalLink, ArrowRight } from 'lucide-react';
import { services, availableTimeSlots } from '../mockData';
import { toast } from 'sonner';
import { bookingAPI } from '../services/api';
import { Checkbox } from '../components/ui/checkbox';

const Services = () => {
  const [searchParams] = useSearchParams();
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [currentBookingId, setCurrentBookingId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingStep, setBookingStep] = useState('intake'); // 'intake' | 'payment'
  const [intakeCompleted, setIntakeCompleted] = useState(false);
  const [blockedSlots, setBlockedSlots] = useState([]);

  // Fetch blocked slots whenever date changes
  useEffect(() => {
    if (!selectedDate) {
      setBlockedSlots([]);
      return;
    }
    const dateStr = selectedDate.toISOString().split('T')[0];
    bookingAPI.getBlockedSlots(dateStr)
      .then(res => setBlockedSlots(res.blockedSlots || []))
      .catch(() => setBlockedSlots([]));
  }, [selectedDate]);

  // Auto-select service from URL ?service=discovery or ?service=individual
  useEffect(() => {
    const param = searchParams.get('service');
    if (!param || selectedService) return;
    const map = {
      discovery: 'Discovery Call',
      individual: 'Individual Therapy Session',
      '3session': '3-Session Momentum Package',
      '5session': '5-Session Deep Transformation Package',
    };
    const targetTitle = map[param];
    if (targetTitle) {
      const found = services.find(s => s.title === targetTitle);
      if (found) {
        setSelectedService(found);
        setTimeout(() => {
          document.getElementById('booking-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 200);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Helper: check if a time slot for the selected date is at least 3 hours from now
  const isSlotAvailable = (slot) => {
    if (!selectedDate) return true;
    // Filter out already-booked slots for this date
    if (blockedSlots.includes(slot)) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const slotDate = new Date(selectedDate);
    slotDate.setHours(0, 0, 0, 0);
    // For future dates, all slots are valid (and not blocked)
    if (slotDate.getTime() > today.getTime()) return true;
    // For today, only show slots at least 3h from now
    const [time, period] = slot.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    let h24 = hours;
    if (period === 'PM' && hours !== 12) h24 += 12;
    if (period === 'AM' && hours === 12) h24 = 0;
    const slotDateTime = new Date(selectedDate);
    slotDateTime.setHours(h24, minutes || 0, 0, 0);
    const threeHoursFromNow = new Date(Date.now() + 3 * 60 * 60 * 1000);
    return slotDateTime > threeHoursFromNow;
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setSelectedDate(null);
    setSelectedTime('');
  };

  const handleFormChange = (e) => {
    setBookingForm({
      ...bookingForm,
      [e.target.name]: e.target.value
    });
  };

  // Services that require scheduling (date + time picker)
  const requiresScheduling = (service) => {
    if (!service) return false;
    return service.title === 'Discovery Call' || service.title === 'Individual Therapy Session';
  };

  const generateUPILink = () => {
    const amount = selectedService?.price || 0;
    const upiId = 'rainbowayesha-2@oksbi';
    const name = 'Ayesha Khan';
    const description = requiresScheduling(selectedService)
      ? `${selectedService?.title} - ${selectedDate?.toLocaleDateString()} at ${selectedTime}`
      : `${selectedService?.title}`;
    
    return `upi://pay?pa=${upiId}&pn=${encodeURIComponent(name)}&am=${amount}&cu=INR&tn=${encodeURIComponent(description)}`;
  };

  const handleBooking = async () => {
    const needsSchedule = requiresScheduling(selectedService);
    
    if (!selectedService || !bookingForm.name || !bookingForm.email || !bookingForm.phone) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (needsSchedule && (!selectedDate || !selectedTime)) {
      toast.error('Please select a date and time slot');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const bookingData = {
        clientName: bookingForm.name,
        email: bookingForm.email,
        phone: bookingForm.phone,
        service: selectedService.title,
        servicePrice: selectedService.price,
        date: needsSchedule ? selectedDate.toISOString().split('T')[0] : '',
        time: needsSchedule ? selectedTime : '',
        message: bookingForm.message || ''
      };
      
      const response = await bookingAPI.create(bookingData);
      
      if (response.success) {
        setCurrentBookingId(response.booking._id);
        // Only show intake form for Individual Therapy Session
        const requiresIntake = selectedService.title === 'Individual Therapy Session';
        setBookingStep(requiresIntake ? 'intake' : 'payment');
        setIntakeCompleted(!requiresIntake); // auto-complete if not required
        setShowPaymentDialog(true);
        toast.success(
          requiresIntake
            ? 'Booking created! Please complete the intake form, then payment.'
            : 'Booking created! Please complete payment to confirm.'
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIntakeNext = () => {
    if (!intakeCompleted) {
      toast.error('Please confirm you have submitted the intake form');
      return;
    }
    setBookingStep('payment');
  };

  const handlePaymentComplete = async () => {
    if (!currentBookingId) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await bookingAPI.updatePayment(currentBookingId, {
        paymentStatus: 'completed',
        paymentId: `UPI_${Date.now()}`
      });
      
      if (response.success) {
        toast.success('Booking confirmed! You will receive a confirmation email shortly.');
        setShowPaymentDialog(false);
        
        // Reset form
        setSelectedService(null);
        setSelectedDate(null);
        setSelectedTime('');
        setBookingForm({ name: '', email: '', phone: '', message: '' });
        setCurrentBookingId(null);
        setBookingStep('intake');
        setIntakeCompleted(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 flex h-20 items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="https://customer-assets.emergentagent.com/job_wellness-journeys/artifacts/amcb4t0p_Generated_image%20%281%29.png"
              alt="Therapy with Ayesha"
              className="h-16 w-auto"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium hover:text-primary">Home</Link>
            <Link to="/services" className="text-sm font-medium text-primary">Services</Link>
            <Link to="/resources" className="text-sm font-medium hover:text-primary">Healing Resources</Link>
            <Link to="/#contact" className="text-sm font-medium hover:text-primary">Contact</Link>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto space-y-12">
          {/* Header */}
          <div className="text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-foreground">
              Services & Booking
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose a service that fits your needs. All appointments require advance payment confirmation to secure your slot.
            </p>
            <Badge variant="secondary" className="text-sm px-4 py-2">
              Need to talk today? Secure same-day sessions are available up to 3 hours before the slot.
            </Badge>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service) => (
              <Card key={service.id} className={`card-hover relative ${selectedService?.id === service.id ? 'border-primary border-2' : ''} ${service.title === 'Individual Therapy Session' ? 'border-primary border-2 shadow-xl' : ''}`}>
                {service.title === 'Individual Therapy Session' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full shadow-md" data-testid="most-popular-ribbon">
                    ⭐ Most Popular
                  </div>
                )}
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-2xl font-serif">{service.title}</CardTitle>
                    {service.savings && (
                      <Badge variant="destructive" className="bg-emerald-600">Save ₹{service.savings}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">{service.duration}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <IndianRupee className="w-4 h-4" />
                      <span className="text-lg font-bold text-foreground">₹{service.price}</span>
                      {service.originalPrice && (
                        <span className="text-sm line-through text-muted-foreground">₹{service.originalPrice}</span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <CardDescription className="text-base">{service.description}</CardDescription>
                  <ul className="space-y-2">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button 
                    onClick={() => handleServiceSelect(service)} 
                    className="w-full bg-primary hover:bg-primary/90"
                    variant={selectedService?.id === service.id ? 'default' : 'outline'}
                  >
                    {selectedService?.id === service.id ? 'Selected' : 'Select Service'}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Booking Form */}
          {selectedService && (
            <Card id="booking-form-section" className="border-primary border-2">
              <CardHeader>
                <CardTitle className="text-2xl font-serif">Book Your Session</CardTitle>
                <CardDescription>Complete the form below to schedule your {selectedService.title}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input 
                      id="name" 
                      name="name"
                      placeholder="Your full name" 
                      value={bookingForm.name}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input 
                      id="email" 
                      name="email"
                      type="email" 
                      placeholder="your.email@example.com" 
                      value={bookingForm.email}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input 
                      id="phone" 
                      name="phone"
                      type="tel" 
                      placeholder="9876543210" 
                      value={bookingForm.phone}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                {requiresScheduling(selectedService) ? (
                  <>
                    <div className="space-y-4">
                      <Label>Select Date *</Label>
                      <div className="border rounded-lg p-4 flex justify-center">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={setSelectedDate}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const d = new Date(date);
                            d.setHours(0, 0, 0, 0);
                            return d < today || date.getDay() === 0;
                          }}
                          className="rounded-md"
                        />
                      </div>
                    </div>

                    {selectedDate && (
                      <div className="space-y-2">
                        <Label htmlFor="time">Select Time Slot *</Label>
                        <Select value={selectedTime} onValueChange={setSelectedTime}>
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a time slot" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableTimeSlots.filter(isSlotAvailable).length === 0 ? (
                              <div className="px-3 py-2 text-sm text-muted-foreground">
                                No slots available for today. Try tomorrow.
                              </div>
                            ) : (
                              availableTimeSlots.filter(isSlotAvailable).map((slot) => (
                                <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-2">
                    <p className="text-sm font-semibold text-foreground">
                      Package Booking
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      After payment, I'll personally reach out to you on your phone/email within 24 hours to schedule your sessions at times that work best for you.
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="message">Additional Notes (Optional)</Label>
                  <Textarea 
                    id="message" 
                    name="message"
                    placeholder="Any specific concerns or questions you'd like to share..." 
                    value={bookingForm.message}
                    onChange={handleFormChange}
                    rows={4}
                  />
                </div>

                <div className="bg-muted p-4 rounded-lg space-y-2">
                  <h4 className="font-semibold text-sm">Booking Summary</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-muted-foreground">Service:</span> {selectedService.title}</p>
                    <p><span className="text-muted-foreground">Duration:</span> {selectedService.duration}</p>
                    {selectedDate && <p><span className="text-muted-foreground">Date:</span> {selectedDate.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>}
                    {selectedTime && <p><span className="text-muted-foreground">Time:</span> {selectedTime}</p>}
                    <p className="text-lg font-bold pt-2"><span className="text-muted-foreground font-normal">Total:</span> ₹{selectedService.price}</p>
                  </div>
                </div>

                <Button 
                  onClick={handleBooking} 
                  className="w-full bg-primary hover:bg-primary/90 text-lg py-6"
                  size="lg"
                  disabled={isSubmitting}
                >
                  <CalendarIcon className="mr-2 h-5 w-5" />
                  {isSubmitting ? 'Creating Booking...' : 'Proceed to Payment'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Multi-step Booking Dialog: Intake -> Payment */}
          <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
            <DialogContent className={bookingStep === 'intake' ? 'max-w-3xl max-h-[90vh] overflow-y-auto' : 'max-w-md'}>
              {/* Step Indicator - only shown if intake is required (Individual Therapy Session) */}
              {selectedService?.title === 'Individual Therapy Session' && (
                <div className="flex items-center justify-center gap-2 pb-2">
                  <div className={`flex items-center gap-2 ${bookingStep === 'intake' ? 'text-primary' : 'text-muted-foreground'}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${bookingStep === 'intake' ? 'bg-primary text-primary-foreground' : 'bg-emerald-100 text-emerald-700 border border-emerald-300'}`}>
                      {bookingStep === 'payment' ? <CheckCircle2 className="w-4 h-4" /> : '1'}
                    </div>
                    <span className="text-xs font-medium">Intake Form</span>
                  </div>
                  <div className="w-12 h-px bg-border" />
                  <div className={`flex items-center gap-2 ${bookingStep === 'payment' ? 'text-primary' : 'text-muted-foreground'}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${bookingStep === 'payment' ? 'bg-primary text-primary-foreground' : 'bg-muted border'}`}>
                      2
                    </div>
                  <span className="text-xs font-medium">Payment</span>
                </div>
              </div>
              )}

              {bookingStep === 'intake' ? (
                <>
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-serif flex items-center gap-2">
                      <FileText className="w-5 h-5 text-primary" />
                      Complete Intake Form
                    </DialogTitle>
                    <DialogDescription>
                      Before payment, please fill out a quick intake form so I can prepare for your session.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-4 py-2">
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                      <p className="text-sm font-medium mb-1">Why this matters</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        The intake form helps me understand your concerns, history, and goals — so our first session can go straight to what matters most to you. All responses are confidential.
                      </p>
                    </div>

                    {/* Embedded form */}
                    <div className="border rounded-lg overflow-hidden bg-background">
                      <iframe
                        src="https://docs.google.com/forms/d/1WJclltaZX4in4LPxoVnRqn12MiEucZ4fMHu_U8kjI00/viewform?embedded=true"
                        width="100%"
                        height="500"
                        frameBorder="0"
                        marginHeight="0"
                        marginWidth="0"
                        title="Client Intake Form"
                        data-testid="booking-intake-iframe"
                      >
                        Loading form...
                      </iframe>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between bg-muted/40 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground">Prefer a full-screen view?</p>
                      <a
                        href="https://docs.google.com/forms/d/1WJclltaZX4in4LPxoVnRqn12MiEucZ4fMHu_U8kjI00/viewform"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="open-intake-newtab"
                      >
                        <Button variant="outline" size="sm">
                          <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                          Open in new tab
                        </Button>
                      </a>
                    </div>

                    {/* Confirmation Checkbox */}
                    <div className="flex items-start gap-3 p-4 border border-primary/30 rounded-lg bg-card">
                      <Checkbox
                        id="intake-completed"
                        checked={intakeCompleted}
                        onCheckedChange={setIntakeCompleted}
                        data-testid="intake-completed-checkbox"
                        className="mt-1"
                      />
                      <Label htmlFor="intake-completed" className="text-sm leading-relaxed cursor-pointer">
                        I have submitted the intake form above and I understand sessions operate on a strict 24-hour cancellation and advance-payment policy.
                      </Label>
                    </div>

                    <Button
                      onClick={handleIntakeNext}
                      className="w-full bg-primary hover:bg-primary/90"
                      size="lg"
                      disabled={!intakeCompleted}
                      data-testid="intake-next-button"
                    >
                      Continue to Payment
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-serif">Complete Payment</DialogTitle>
                    <DialogDescription>
                      Pay ₹{selectedService?.price} to confirm your booking
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-6 py-4">
                    <div className="bg-muted p-6 rounded-lg text-center space-y-4">
                      <img
                        src="https://customer-assets.emergentagent.com/job_wellness-journeys/artifacts/a5zjbopz_WhatsApp%20Image%202026-05-18%20at%209.41.09%20PM.jpeg"
                        alt="UPI QR Code — Ayesha Khan"
                        className="w-56 h-56 mx-auto rounded-lg shadow-md object-cover"
                        data-testid="services-upi-qr"
                      />
                      <p className="text-sm text-muted-foreground">Scan to pay with any UPI app</p>
                      <div className="bg-background p-4 rounded border">
                        <p className="text-xs text-muted-foreground mb-1">UPI ID</p>
                        <p className="font-mono font-semibold">rainbowayesha-2@oksbi</p>
                      </div>
                    </div>

                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-2">Or pay using</p>
                      <a 
                        href={generateUPILink()} 
                        className="inline-block w-full"
                      >
                        <Button variant="outline" className="w-full">
                          Open UPI App
                        </Button>
                      </a>
                    </div>

                    <div className="space-y-2">
                      <Button 
                        onClick={handlePaymentComplete} 
                        className="w-full bg-primary hover:bg-primary/90"
                        disabled={isSubmitting}
                        data-testid="payment-complete-button"
                      >
                        {isSubmitting ? 'Confirming...' : "I've Completed Payment"}
                      </Button>
                      <button
                        onClick={() => setBookingStep('intake')}
                        className="w-full text-xs text-muted-foreground hover:text-primary text-center pt-1"
                      >
                        ← Back to intake form
                      </button>
                      <p className="text-xs text-center text-muted-foreground">
                        Your booking will be confirmed once payment is verified
                      </p>
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Services;
